import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportCenter = viewportWidth / 2;
    const isMobileViewport = viewportWidth < 1200;

    this.style.top = OFFSET + 'px';

    // Determine if panel is closer to left or right side
    const panelCenter = rect.left + rect.width / 2;
    const isCloserToRight = panelCenter > viewportCenter;

    // Clear previous positioning
    this.style.left = '';
    this.style.right = '';

    if (isCloserToRight && isMobileViewport) {
      // Panel is closer to right and viewport is < 1200px - snap to right edge
      this.style.right = '0';
    } else {
      // Panel is closer to left - adjust left positioning if needed
      if (rect.left < 0) {
        const overflowAmount = Math.abs(rect.left) + OFFSET;
        this.style.left = `0`;
      }
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #summaryMutationObserver = new MutationObserver(
    debounce(() => {
      this.#updatePosition();
    }, 0)
  );

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 0);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
      
      // Watch for changes in the filter summary button
      const summary = this.closest('details')?.querySelector('.facets__summary');
      if (summary) {
        this.#summaryMutationObserver.observe(summary, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
    this.#summaryMutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
